import React, { useEffect, useRef, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import SidebarLinkGroup from "./SidebarLinkGroup";
import {
  LayoutDashboard,
  FolderKanban,
  FileText,
  ShieldCheck,
  UsersRound,
} from "lucide-react";
import Logo from "../../assests/Logos.svg";
import { hasPermission } from "../../utils/permission";

interface SidebarProps {
  sidebarOpen: boolean;
  setSidebarOpen: (arg: boolean) => void;
}
const Sidebar = ({ sidebarOpen, setSidebarOpen }: SidebarProps) => {
  const location = useLocation();
  const { pathname } = location;
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const trigger = useRef<any>(null);
  const sidebar = useRef<any>(null);
  const storedSidebarExpanded = localStorage.getItem("sidebar-expanded");
  const [sidebarExpanded, setSidebarExpanded] = useState(
    storedSidebarExpanded === null ? false : storedSidebarExpanded === "true"
  );

  // close on click outside
  useEffect(() => {
    const clickHandler = ({ target }: MouseEvent) => {
      if (!sidebar.current || !trigger.current) return;
      if (
        !sidebarOpen ||
        sidebar.current.contains(target) ||
        trigger.current.contains(target)
      )
        return;
      setSidebarOpen(false);
    };
    document.addEventListener("click", clickHandler);
    return () => document.removeEventListener("click", clickHandler);
  });
  // close if the esc key is pressed
  useEffect(() => {
    const keyHandler = ({ keyCode }: KeyboardEvent) => {
      if (!sidebarOpen || keyCode !== 27) return;
      setSidebarOpen(false);
    };
    document.addEventListener("keydown", keyHandler);
    return () => document.removeEventListener("keydown", keyHandler);
  });

  useEffect(() => {
    localStorage.setItem("sidebar-expanded", sidebarExpanded.toString());
    if (sidebarExpanded) {
      document.querySelector("body")?.classList.add("sidebar-expanded");
    } else {
      document.querySelector("body")?.classList.remove("sidebar-expanded");
    }
  }, [sidebarExpanded]);
  useEffect(() => {
    if (
      pathname.includes("/dashboard/createrole") ||
      pathname.includes("/dashboard/rolemanagement") ||
      pathname.includes("/dashboard/updaterole")
    ) {
      setOpenDropdown("role");
    } else if (
      pathname.includes("/dashboard/userCreate") ||
      pathname.includes("/dashboard/userListing") ||
      pathname.includes("/dashboard/updateUser")
    ) {
      setOpenDropdown("user");
    } else if (
      pathname.includes("/dashboard/projectCreate") ||
      pathname.includes("/dashboard/projectList") ||
      pathname.includes("/dashboard/projectUpdate")
    ) {
      setOpenDropdown("project");
    } else if (
      pathname.includes("/dashboard/formCreation") ||
      pathname.includes("/dashboard/surveyListing") ||
      pathname.includes("/dashboard/createsurvey") ||
      pathname.includes("/dashboard/updatesurvey") ||
      pathname.includes("/dashboard/surveyHistory") ||
      pathname.includes("/dashboard/surveyDetails") ||
      pathname.includes("/dashboard/formUpdation")
    ) {
      setOpenDropdown("survey");
    } else {
      setOpenDropdown(null); // fallback
    }
  }, [pathname]);

  return (
    <aside
      ref={sidebar}
      className={`absolute left-0 top-0 z-9999 flex h-screen w-72.5 flex-col overflow-y-hidden bg-white duration-300 ease-linear dark:bg-boxdark lg:static lg:translate-x-0 ${
        sidebarOpen ? "translate-x-0" : "-translate-x-full"
      }`}
    >
      {/* <!-- SIDEBAR HEADER --> */}
      <div className="flex items-center justify-between gap-2 px-6 py-5.5 lg:py-6.5">
        <div className="w-full h-20 flex items-center justify-center px-4">
          <NavLink to="/">
            <img src={Logo} alt="Logo" className="h-18 w-auto object-contain" />
          </NavLink>
        </div>

        <button
          ref={trigger}
          onClick={() => setSidebarOpen(!sidebarOpen)}
          aria-controls="sidebar"
          aria-expanded={sidebarOpen}
          className="block lg:hidden"
        >
          <svg
            className="fill-current"
            width="20"
            height="18"
            viewBox="0 0 20 18"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M19 8.175H2.98748L9.36248 1.6875C9.69998 1.35 9.69998 0.825 9.36248 0.4875C9.02498 0.15 8.49998 0.15 8.16248 0.4875L0.399976 8.3625C0.0624756 8.7 0.0624756 9.225 0.399976 9.5625L8.16248 17.4375C8.31248 17.5875 8.53748 17.7 8.76248 17.7C8.98748 17.7 9.17498 17.625 9.36248 17.475C9.69998 17.1375 9.69998 16.6125 9.36248 16.275L3.02498 9.8625H19C19.45 9.8625 19.825 9.4875 19.825 9.0375C19.825 8.55 19.45 8.175 19 8.175Z"
              fill=""
            />
          </svg>
        </button>
      </div>
      <div className="no-scrollbar flex flex-col overflow-y-auto duration-300 ease-linear">
        <nav className=" py-4 px-4 lg:px-6">
          <div>
            <ul className="mb-6 flex flex-col gap-1.5">
              <li>
                <NavLink
                  to="/dashboard"
                  className={`group relative flex items-center gap-2.5 rounded-md py-2 px-4 font-medium text-black duration-300 ease-in-out hover:bg-black hover:text-white dark:hover:bg-meta-4 ${
                    (pathname === "/dashboard" || pathname === "/") &&
                    "text-white bg-graydark dark:bg-meta-4"
                  }`}
                >
                  {" "}
                  <LayoutDashboard size={18} className="text-current" />
                  Dashboard
                </NavLink>
              </li>
              {(hasPermission("role-create") ||
                hasPermission("role-update") ||
                hasPermission("role-delete") ||
                hasPermission("role-view")) && (
                <SidebarLinkGroup
                  groupKey="role"
                  openDropdown={openDropdown}
                  setOpenDropdown={setOpenDropdown}
                  activeCondition={
                    pathname.includes("/dashboard/createrole") ||
                    pathname.includes("/dashboard/rolemanagement") ||
                    pathname.includes("/dashboard/updaterole")
                  }
                >
                  {(handleClick, open) => (
                    <>
                      <NavLink
                        to="#"
                        className={`group relative flex items-center gap-2.5 rounded-md py-2 px-4 font-medium text-black duration-300 ease-in-out hover:bg-black hover:text-white dark:hover:bg-meta-4 ${
                          (pathname.includes("/dashboard/createrole") ||
                            pathname.includes("/dashboard/rolemanagement") ||
                            pathname.includes("/dashboard/updaterole")) &&
                          "text-white bg-graydark dark:bg-meta-4"
                        }`}
                        onClick={(e) => {
                          e.preventDefault();
                          sidebarExpanded
                            ? handleClick()
                            : setSidebarExpanded(true);
                        }}
                      >
                        <ShieldCheck size={18} className="text-current" />
                        Role Management
                        <svg
                          className={`absolute right-4 top-1/2 -translate-y-1/2 fill-current ${
                            open && "rotate-180"
                          }`}
                          width="20"
                          height="20"
                          viewBox="0 0 20 20"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M4.41107 6.9107C4.73651 6.58527 5.26414 6.58527 5.58958 6.9107L10.0003 11.3214L14.4111 6.91071C14.7365 6.58527 15.2641 6.58527 15.5896 6.91071C15.915 7.23614 15.915 7.76378 15.5896 8.08922L10.5896 13.0892C10.2641 13.4147 9.73651 13.4147 9.41107 13.0892L4.41107 8.08922C4.08563 7.76378 4.08563 7.23614 4.41107 6.9107Z"
                            fill="#A7A7A7"
                          />
                        </svg>
                      </NavLink>

                      <div
                        className={`translate transform overflow-hidden ${
                          !open && "hidden"
                        }`}
                      >
                        <ul className="my-3 flex flex-col gap-2.5 pl-6">
                          {hasPermission("role-create") && (
                            <li>
                              <NavLink
                                to="/dashboard/createrole"
                                end
                                className={() =>
                                  "group relative flex items-center gap-2.5 rounded-md px-4 font-medium text-black duration-300 ease-in-out hover:text-primary " +
                                  (pathname === "/dashboard/createrole" ||
                                  pathname === "/dashboard/updaterole"
                                    ? "!text-primary"
                                    : "")
                                }
                              >
                                Create Role
                              </NavLink>
                            </li>
                          )}
                          {hasPermission("role-view") && (
                            <li>
                              <NavLink
                                to="/dashboard/rolemanagement"
                                end
                                className={({ isActive }) =>
                                  "group relative flex items-center gap-2.5 rounded-md px-4 font-medium text-black duration-300 ease-in-out hover:text-primary " +
                                  (isActive && "!text-primary")
                                }
                              >
                                Manage Role
                              </NavLink>
                            </li>
                          )}
                        </ul>
                      </div>
                    </>
                  )}
                </SidebarLinkGroup>
              )}
              {(hasPermission("user-create") ||
                hasPermission("user-update") ||
                hasPermission("user-delete") ||
                hasPermission("user-view")) && (
                <SidebarLinkGroup
                  groupKey="user"
                  openDropdown={openDropdown}
                  setOpenDropdown={setOpenDropdown}
                  activeCondition={
                    pathname.includes("/dashboard/userCreate") ||
                    pathname.includes("/dashboard/userListing") ||
                    pathname.includes("/dashboard/updateUser")
                  }
                >
                  {(handleClick, open) => {
                    return (
                      <React.Fragment>
                        <NavLink
                          to="#"
                          className={`group relative flex items-center gap-2.5 rounded-md py-2 px-4 font-medium text-black duration-300 ease-in-out hover:bg-black hover:text-white dark:hover:bg-meta-4 ${
                            (pathname.includes("/dashboard/userCreate") ||
                              pathname.includes("/dashboard/userListing") ||
                              pathname.includes("/dashboard/updateUser")) &&
                            "text-white bg-graydark dark:bg-meta-4"
                          }`}
                          onClick={(e) => {
                            e.preventDefault();
                            sidebarExpanded
                              ? handleClick()
                              : setSidebarExpanded(true);
                          }}
                        >
                          <UsersRound size={18} className="text-current" />
                          User Management
                          <svg
                            className={`absolute right-4 top-1/2 -translate-y-1/2 fill-current ${
                              open && "rotate-180"
                            }`}
                            width="20"
                            height="20"
                            viewBox="0 0 20 20"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              fillRule="evenodd"
                              clipRule="evenodd"
                              d="M4.41107 6.9107C4.73651 6.58527 5.26414 6.58527 5.58958 6.9107L10.0003 11.3214L14.4111 6.91071C14.7365 6.58527 15.2641 6.58527 15.5896 6.91071C15.915 7.23614 15.915 7.76378 15.5896 8.08922L10.5896 13.0892C10.2641 13.4147 9.73651 13.4147 9.41107 13.0892L4.41107 8.08922C4.08563 7.76378 4.08563 7.23614 4.41107 6.9107Z"
                              fill="#A7A7A7"
                            />
                          </svg>
                        </NavLink>
                        <div
                          className={`translate transform overflow-hidden ${
                            !open && "hidden"
                          }`}
                        >
                          <ul className="my-3 flex flex-col gap-2.5 pl-6">
                            {hasPermission("user-create") && (
                              <li>
                                <NavLink
                                  to="/dashboard/userCreate"
                                  end
                                  className={() =>
                                    "group relative flex items-center gap-2.5 rounded-md px-4 font-medium text-black duration-300 ease-in-out hover:text-primary " +
                                    (pathname === "/dashboard/userCreate" ||
                                    pathname === "/dashboard/updateUser"
                                      ? "!text-primary"
                                      : "")
                                  }
                                >
                                  Create User
                                </NavLink>
                              </li>
                            )}
                            {hasPermission("user-view") && (
                              <li>
                                <NavLink
                                  to="/dashboard/userListing"
                                  end
                                  className={({ isActive }) =>
                                    "group relative flex items-center gap-2.5 rounded-md px-4 font-medium text-black duration-300 ease-in-out hover:text-primary " +
                                    (isActive && "!text-primary")
                                  }
                                >
                                  Manage User
                                </NavLink>
                              </li>
                            )}
                          </ul>
                        </div>
                      </React.Fragment>
                    );
                  }}
                </SidebarLinkGroup>
              )}
              {(hasPermission("project-create") ||
                hasPermission("project-update") ||
                hasPermission("project-delete") ||
                hasPermission("project-view")) && (
                <SidebarLinkGroup
                  groupKey="project"
                  openDropdown={openDropdown}
                  setOpenDropdown={setOpenDropdown}
                  activeCondition={
                    pathname.includes("/dashboard/projectCreate") ||
                    pathname.includes("/dashboard/projectList") ||
                    pathname.includes("/dashboard/projectUpdate")
                  }
                >
                  {(handleClick, open) => {
                    return (
                      <React.Fragment>
                        <NavLink
                          to="#"
                          className={`group relative flex items-center gap-2.5 rounded-md py-2 px-4 font-medium text-black duration-300 ease-in-out hover:bg-black hover:text-white dark:hover:bg-meta-4 ${
                            (pathname.includes("/dashboard/projectCreate") ||
                              pathname.includes("/dashboard/projectList") ||
                              pathname.includes("/dashboard/projectUpdate")) &&
                            "bg-graydark text-white dark:bg-meta-4"
                          }`}
                          onClick={(e) => {
                            e.preventDefault();
                            sidebarExpanded
                              ? handleClick()
                              : setSidebarExpanded(true);
                          }}
                        >
                          <FolderKanban size={18} className="text-current" />
                          Project Management
                          <svg
                            className={`absolute right-4 top-1/2 -translate-y-1/2 fill-current ${
                              open && "rotate-180"
                            }`}
                            width="20"
                            height="20"
                            viewBox="0 0 20 20"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              fillRule="evenodd"
                              clipRule="evenodd"
                              d="M4.41107 6.9107C4.73651 6.58527 5.26414 6.58527 5.58958 6.9107L10.0003 11.3214L14.4111 6.91071C14.7365 6.58527 15.2641 6.58527 15.5896 6.91071C15.915 7.23614 15.915 7.76378 15.5896 8.08922L10.5896 13.0892C10.2641 13.4147 9.73651 13.4147 9.41107 13.0892L4.41107 8.08922C4.08563 7.76378 4.08563 7.23614 4.41107 6.9107Z"
                              fill="#A7A7A7"
                            />
                          </svg>
                        </NavLink>

                        <div
                          className={`translate transform overflow-hidden ${
                            !open && "hidden"
                          }`}
                        >
                          <ul className="my-3 flex flex-col gap-2.5 pl-6">
                            {hasPermission("project-create") && (
                              <li>
                                <NavLink
                                  to="/dashboard/projectCreate"
                                  end
                                  className={() =>
                                    "group relative flex items-center gap-2.5 rounded-md px-4 font-medium text-black duration-300 ease-in-out hover:text-primary " +
                                    (pathname === "/dashboard/projectCreate" ||
                                    pathname === "/dashboard/projectUpdate"
                                      ? "!text-primary"
                                      : "")
                                  }
                                >
                                  Create Project
                                </NavLink>
                              </li>
                            )}
                            {hasPermission("project-view") && (
                              <li>
                                <NavLink
                                  to="/dashboard/projectList"
                                  end
                                  className={({ isActive }) =>
                                    "group relative flex items-center gap-2.5 rounded-md px-4 font-medium text-black duration-300 ease-in-out hover:text-primary " +
                                    (isActive && "!text-primary")
                                  }
                                >
                                  Manage Project
                                </NavLink>
                              </li>
                            )}
                          </ul>
                        </div>
                      </React.Fragment>
                    );
                  }}
                </SidebarLinkGroup>
              )}
              {(hasPermission("survey-create") ||
                hasPermission("survey-update") ||
                hasPermission("survey-delete") ||
                hasPermission("survey-view")) && (
                <SidebarLinkGroup
                  groupKey="survey"
                  openDropdown={openDropdown}
                  setOpenDropdown={setOpenDropdown}
                  activeCondition={
                    pathname.includes("/dashboard/formCreation") ||
                    pathname.includes("/dashboard/surveyListing") ||
                    pathname.includes("/dashboard/createsurvey") ||
                    pathname.includes("/dashboard/updatesurvey") ||
                    pathname.includes("/dashboard/surveyHistory") ||
                    pathname.includes("/dashboard/surveyDetails") ||
                    pathname.includes("/dashboard/formUpdation")
                  }
                >
                  {(handleClick, open) => {
                    return (
                      <React.Fragment>
                        <NavLink
                          to="#"
                          className={`group relative flex items-center gap-2.5 rounded-md py-2 px-4 font-medium text-black duration-300 ease-in-out hover:bg-black hover:text-white dark:hover:bg-meta-4  ${
                            (pathname.includes("/dashboard/formCreation") ||
                              pathname.includes("/dashboard/surveyListing") ||
                              pathname.includes("/dashboard/createsurvey") ||
                              pathname.includes("/dashboard/updatesurvey") ||
                              pathname.includes("/dashboard/surveyHistory") ||
                              pathname.includes("/dashboard/surveyDetails") ||
                              pathname.includes("/dashboard/formUpdation")) &&
                            "text-white bg-graydark dark:bg-meta-4"
                          }`}
                          onClick={(e) => {
                            e.preventDefault();
                            sidebarExpanded
                              ? handleClick()
                              : setSidebarExpanded(true);
                          }}
                        >
                          <FileText size={18} className="text-current" />
                          Survey Management
                          <svg
                            className={`absolute right-4 top-1/2 -translate-y-1/2 fill-current ${
                              open && "rotate-180"
                            }`}
                            width="20"
                            height="20"
                            viewBox="0 0 20 20"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              fillRule="evenodd"
                              clipRule="evenodd"
                              d="M4.41107 6.9107C4.73651 6.58527 5.26414 6.58527 5.58958 6.9107L10.0003 11.3214L14.4111 6.91071C14.7365 6.58527 15.2641 6.58527 15.5896 6.91071C15.915 7.23614 15.915 7.76378 15.5896 8.08922L10.5896 13.0892C10.2641 13.4147 9.73651 13.4147 9.41107 13.0892L4.41107 8.08922C4.08563 7.76378 4.08563 7.23614 4.41107 6.9107Z"
                              fill="#A7A7A7"
                            />
                          </svg>
                        </NavLink>

                        <div
                          className={`translate transform overflow-hidden ${
                            !open && "hidden"
                          }`}
                        >
                          <ul className="my-3 flex flex-col gap-2.5 pl-6">
                            {hasPermission("survey-create") && (
                              <li>
                                <NavLink
                                  to="/dashboard/createsurvey"
                                  end
                                  className={() =>
                                    "group relative flex items-center gap-2.5 rounded-md px-4 font-medium text-black duration-300 ease-in-out hover:text-primary " +
                                    (pathname === "/dashboard/createsurvey" ||
                                    pathname === "/dashboard/updatesurvey"
                                      ? "!text-primary"
                                      : "")
                                  }
                                >
                                  Create Survey
                                </NavLink>
                              </li>
                            )}
                            {hasPermission("survey-create") && (
                              <li>
                                <NavLink
                                  to="/dashboard/formCreation"
                                  end
                                  className={({ isActive }) =>
                                    "group relative flex items-center gap-2.5 rounded-md px-4 font-medium text-black duration-300 ease-in-out hover:text-primary " +
                                    (isActive && "!text-primary")
                                  }
                                >
                                  Create Form
                                </NavLink>
                              </li>
                            )}
                            {hasPermission("survey-view") && (
                              <li>
                                <NavLink
                                  to="/dashboard/surveyListing"
                                  end
                                  className={({ isActive }) =>
                                    "group relative flex items-center gap-2.5 rounded-md px-4 font-medium text-black duration-300 ease-in-out hover:text-primary " +
                                    (pathname === "/dashboard/surveyListing" ||
                                    pathname === "/dashboard/formUpdation"
                                      ? "!text-primary"
                                      : "")
                                  }
                                >
                                  Manage Survey
                                </NavLink>
                              </li>
                            )}
                            {hasPermission("survey-view") && (
                              <li>
                                <NavLink
                                  to="/dashboard/surveyHistory"
                                  end
                                  className={({ isActive }) =>
                                    "group relative flex items-center gap-2.5 rounded-md px-4 font-medium text-black duration-300 ease-in-out hover:text-primary " +
                                    (pathname === "/dashboard/surveyDetails" ||
                                    pathname === "/dashboard/surveyHistory"
                                      ? "!text-primary"
                                      : "")
                                  }
                                >
                                  Survey History
                                </NavLink>
                              </li>
                            )}
                          </ul>
                        </div>
                      </React.Fragment>
                    );
                  }}
                </SidebarLinkGroup>
              )}
              {hasPermission("role-view") && (
                <li>
                  <NavLink
                    to="/dashboard/reportmanagement"
                    className={`group relative flex items-center gap-2.5 rounded-md py-2 px-4 font-medium text-black duration-300 ease-in-out hover:bg-black hover:text-white dark:hover:bg-meta-4 ${
                      pathname.includes("/dashboard/reportmanagement") &&
                      "text-white bg-graydark dark:bg-meta-4"
                    }`}
                  >
                    {" "}
                    <FileText size={18} className="text-current" />
                    Report Management
                  </NavLink>
                </li>
              )}
            </ul>
          </div>
        </nav>
      </div>
    </aside>
  );
};

export default Sidebar;
